/**
 * Studio Recording Tutorial
 * Guides users through recording a real video reflection in the Studio.
 * Temporarily enables AI features (transcription, follow-up questions) to
 * demonstrate the full workflow, regardless of user settings.
 * Written for older, less tech-savvy users with simple language.
 *
 * Content tone:
 * - requiresClick steps use direct instructions ("Tap this to...")
 * - Informational steps use descriptive language ("You can use this to...")
 */

export const studioTutorial = {
  id: 'studio-recording',
  name: 'How to Use the Studio',
  description: 'Learn how to record reflections using video, audio, or text in the Studio',

  // Temporarily override user AI preferences during this tutorial
  aiOverrides: {
    authorize_ai: true,
    transcription_preference: 'always',
    enable_followups: true,
    auto_enhance: true
  },

  steps: [
    {
      id: 'navigation',
      selector: '.view-toggle',
      blockChildSelectors: ['.view-toggle button:not(:nth-child(2))'],
      title: 'Navigation',
      content: 'These buttons let you switch between the main pages: Home, Prompts, and Reflections. Tap Prompts to continue.',
      position: 'bottom',
      highlightPadding: 4,
      requiresClick: true
    },
    {
      id: 'categories',
      selector: '.categories',
      blockChildSelectors: ['.categories button:not(.active)'],
      title: 'Categories',
      content: 'Prompts are organized into categories. Each button shows a different group of questions. Tap the selected category to continue.',
      position: 'bottom',
      highlightPadding: 4,
      requiresClick: true
    },
    {
      id: 'find-prompt',
      selector: '.prompt-card:not([data-has-reflections])',
      fallbackSelectors: ['.prompt-card'],
      blockChildSelectors: ['.reflection-btn-edit', '.reflection-btn-show'],
      title: 'Find a Prompt',
      content: 'Tap a question you want to answer. This opens the prompt so you can add a reflection.',
      position: 'right',
      highlightPadding: 8,
      requiresClick: true
    },
    {
      id: 'ai-disclosure',
      selector: null,
      title: 'AI Features',
      content: 'For this tutorial, AI features are temporarily enabled to demonstrate transcription and follow-up questions. When you record a reflection, your words will be sent to AI for processing. You can opt in or out of these features anytime in your user settings.',
      position: 'center'
    },
    {
      id: 'open-studio',
      selector: '.prompt-card.expanded .reflection-btn-edit',
      fallbackSelectors: ['.reflection-btn-edit'],
      title: 'Open the Studio',
      content: 'Tap the Add button to open the Studio, where you will record your reflection.',
      position: 'left',
      waitForElement: true,
      maxWaitTime: 3000,
      highlightPadding: 4,
      requiresClick: true
    },
    {
      id: 'recording-modes',
      selector: '.studio-mode-btn',
      title: 'Choose How to Record',
      content: 'These buttons let you choose how to record: Video, Audio, Image, or Text. You can switch between them at any time. Video is selected by default.',
      position: 'bottom',
      waitForElement: true,
      maxWaitTime: 5000,
      highlightPadding: 8,
      disableTargetClick: true
    },
    {
      id: 'start-recording',
      selector: '.studio-recording-area .btn-primary',
      title: 'Start Recording',
      content: 'Tap the Start Recording button. A short countdown will begin, then recording will start automatically.',
      position: 'left',
      waitForElement: true,
      maxWaitTime: 5000,
      highlightPadding: 8,
      requiresClick: true
    },
    {
      id: 'stop-recording',
      selector: '.studio-recording-area',
      title: 'Record Your Reflection',
      content: 'Speak your reflection, then tap Stop Recording when you are done.',
      position: 'left',
      waitForElement: true,
      maxWaitTime: 10000,
      highlightPadding: 8,
      requiresClick: true
    },
    {
      id: 'processing',
      selector: null,
      title: 'Processing Your Recording',
      content: 'Your recording is being saved. Because AI features are enabled, your words will be transcribed into text and follow-up questions will be generated. This may take a few moments.',
      position: 'center'
    },
    {
      id: 'show-transcription',
      selector: '.studio-transcription-toggle',
      title: 'View the Transcription',
      content: 'Your recording has been processed. Tap this button to see the text that AI transcribed from your words.',
      position: 'left',
      waitForElement: true,
      maxWaitTime: 60000,
      highlightPadding: 8,
      optional: true,
      requiresClick: true
    },
    {
      id: 'transcription-text',
      selector: '.studio-transcription-text',
      title: 'Transcribed Text',
      content: 'This is the text that AI transcribed from your recording. It listened to your words and converted them into text. You can enable or disable this feature in your user settings.',
      position: 'left',
      waitForElement: true,
      maxWaitTime: 3000,
      highlightPadding: 8,
      optional: true,
      disableTargetClick: true
    },
    {
      id: 'followup-questions',
      selector: '.studio-suggestions-panel',
      title: 'Follow-up Questions',
      content: 'AI has generated follow-up questions based on your reflection. You can tap "Respond" next to any question to answer it and continue reflecting deeper.',
      position: 'left',
      waitForElement: true,
      maxWaitTime: 60000,
      highlightPadding: 8,
      optional: true,
      disableTargetClick: true
    },
    {
      id: 'new-questions',
      selector: '.studio-enhance-btn',
      title: 'Refresh Questions',
      content: 'You can also tap this button to generate a new set of follow-up questions if you would like different ones. You can enable or disable follow-up questions in your user settings.',
      position: 'left',
      waitForElement: true,
      maxWaitTime: 5000,
      highlightPadding: 8,
      optional: true,
      disableTargetClick: true
    },
    {
      id: 'title-row',
      selector: '.studio-title-row',
      title: 'Title Your Reflection',
      content: 'This is where you can give your reflection a title, or let AI suggest one. You can edit it any time.',
      position: 'right',
      waitForElement: true,
      maxWaitTime: 5000,
      highlightPadding: 4,
      disableTargetClick: true
    },
    {
      id: 'back-button',
      selector: '.studio-back-btn',
      title: 'Save and Return',
      content: 'When you are finished, you can use this button to go back. Your reflection is saved automatically as you work.',
      position: 'right',
      waitForElement: true,
      maxWaitTime: 5000,
      highlightPadding: 8,
      disableTargetClick: true
    },
    {
      id: 'help-button',
      selector: '.help-btn',
      title: 'More Tutorials',
      content: 'Tap the "?" button any time to find more tutorials and learn about other features.',
      position: 'bottom',
      highlightPadding: 4
    },
    {
      id: 'complete',
      selector: null,
      title: 'You\'re Ready!',
      content: 'You now know how to use the Studio! Record video, audio, or text. AI can transcribe your words and suggest follow-up questions to help you reflect deeper. You can manage these features in your user settings.',
      position: 'center',
      isCompletionStep: true
    }
  ]
};
